export const customComparator = (valueA, valueB) => {
  return String(valueA)?.toLowerCase?.()?.localeCompare?.(String(valueB)?.toLowerCase?.());
};

/** Epoch ms for a raw date value, or null if missing/invalid. */
export const parseDateSortEpochMs = (raw) => {
  if (!raw) return null;
  const t = new Date(raw).getTime();
  return Number.isNaN(t) ? null : t;
};

/** Epoch ms for `rowData[field]`, or null if missing/invalid (AG Grid date sort). */
export const getRowDateSortTime = (rowData, field) => parseDateSortEpochMs(rowData?.[field]);

/** AG Grid comparator: sort by date at top-level `field`; nulls last ascending. */
export const agGridDateFieldComparator = (field) => (_valueA, _valueB, nodeA, nodeB) => {
  const timeA = getRowDateSortTime(nodeA?.data, field);
  const timeB = getRowDateSortTime(nodeB?.data, field);
  if (timeA == null && timeB == null) return 0;
  if (timeA == null) return 1;
  if (timeB == null) return -1;
  return timeA - timeB;
};

/**
 * AG Grid comparator when the sortable value comes from multiple row paths
 * (e.g. `createdAt` OR `date`). `getRawFromRow` should return an ISO-like string or Date.
 */
export const agGridDateComparatorFromRow = (getRawFromRow) => (_a, _b, nodeA, nodeB) => {
  const timeA = parseDateSortEpochMs(getRawFromRow(nodeA?.data));
  const timeB = parseDateSortEpochMs(getRawFromRow(nodeB?.data));
  if (timeA == null && timeB == null) return 0;
  if (timeA == null) return 1;
  if (timeB == null) return -1;
  return timeA - timeB;
};

export const agGridDateCreatedAtFieldComparator = agGridDateFieldComparator('createdAt');

export const formatFilterValue = (filterModel) => {
  if (filterModel.filterType === 'text') {
    const filterValue = filterModel.filter || filterModel.filter1 || '';
    if (!filterValue) return '';
    const type = filterModel.type || 'contains';
    if (type === 'contains') return filterValue;
    if (type === 'equals') return `= ${filterValue}`;
    if (type === 'startsWith') return `inicia con: ${filterValue}`;
    if (type === 'endsWith') return `termina con: ${filterValue}`;
    if (type === 'notContains') return `no contiene: ${filterValue}`;
    return filterValue;
  }
  if (filterModel.filterType === 'number') {
    const filterValue = filterModel.filter;
    if (!filterValue && filterValue !== 0) return '';
    const type = filterModel.type || 'equals';
    if (type === 'equals') return filterValue;
    if (type === 'greaterThan') return `> ${filterValue}`;
    if (type === 'lessThan') return `< ${filterValue}`;
    if (type === 'inRange') return `${filterValue} - ${filterModel.filterTo}`;
    return filterValue;
  }
  return '';
};

export const getActiveFilters = (api, columnNameMap) => {
  if (!api) return {};
  const filterModel = api.getFilterModel();
  const formattedFilters = {};

  Object.keys(filterModel).forEach((field) => {
    const filter = filterModel[field];
    const columnName = columnNameMap[field] || field;
    const filterValue = formatFilterValue(filter);
    if (filterValue) {
      formattedFilters[columnName] = filterValue;
    }
  });

  return formattedFilters;
};

const getAgGridBodyViewport = () => document.querySelector('.ag-theme-alpine .ag-body-viewport');

/** Capture pagination page + window/grid scroll before leaving a list view. */
export const captureAgGridViewState = (api) => {
  const viewport = getAgGridBodyViewport();
  return {
    page: api?.paginationGetCurrentPage?.() ?? 0,
    scrollY: window.scrollY || 0,
    scrollLeft: viewport?.scrollLeft ?? 0,
  };
};

/** Restore pagination page + scroll after the grid remounts. */
export const restoreAgGridViewState = (api, viewState) => {
  if (!api || !viewState) return;

  // Defer so filter restores that reset pagination finish first.
  requestAnimationFrame(() => {
    if (typeof viewState.page === 'number' && viewState.page > 0) {
      api.paginationGoToPage(viewState.page);
    }
    window.scrollTo(0, viewState.scrollY || 0);
    const viewport = getAgGridBodyViewport();
    if (viewport && typeof viewState.scrollLeft === 'number') {
      viewport.scrollLeft = viewState.scrollLeft;
    }
  });
};
